import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { NPSDTabBar } from '@/components/npsd/NPSDTabBar';
import { NPSDSearchPerson } from '@/components/npsd/NPSDSearchPerson';
import { NPSDSearchVehicle } from '@/components/npsd/NPSDSearchVehicle';
import { NPSDSearchBusiness } from '@/components/npsd/NPSDSearchBusiness';
import { NPSDSearchClient } from '@/components/npsd/NPSDSearchClient';
import { NPSDSearchReport } from '@/components/npsd/NPSDSearchReport';
import { NPSDSearchCallCard } from '@/components/npsd/NPSDSearchCallCard';
import { NPSDSearchTourStop } from '@/components/npsd/NPSDSearchTourStop';
import {
  navigate,
  navigateToRecord,
  navigateToCaseReport,
  navigateToCallCard,
  navigateToTourStopCompletion,
  navigateToNewPerson,
  navigateToNewVehicle,
  navigateToNewBusiness,
} from '@/services/navigation';
import { currentUserHasPermission, getModuleType, getOfficerLabel } from '@/utils/legacy-utils';
import { openPageInNewWindow } from '@/utils/dom';
import { buildHashPath } from '@/router/route-config';
import { AccessAuthorities } from '@/models';

const DEFAULT_VISIBLE_TABS = ['person', 'vehicle', 'business', 'client', 'report', 'callcard', 'tourstop'];
const DEFAULT_INITIAL_VALUES: Record<string, any> = {};

interface NPSDSearchProps {
  searchMode?: string;
  initialTab?: string;
  visibleTabs?: string[];
  initialValues?: Record<string, any>;
  onView?: (payload: { type: string; item: any }) => void;
  onAdd?: (payload: { type: string; item: any }) => void;
  onLink?: (payload: { type: string; item: any }) => void;
  onOpenNewTab?: (payload: { type: string; item: any }) => void;
  onNew?: (type: string) => void;
  onTabChange?: (tabId: string) => void;
}

export function NPSDSearch({
  searchMode = 'default',
  initialTab = 'person',
  visibleTabs = DEFAULT_VISIBLE_TABS,
  initialValues = DEFAULT_INITIAL_VALUES,
  onView,
  onAdd,
  onLink,
  onOpenNewTab,
  onNew,
  onTabChange,
}: NPSDSearchProps) {
  const { theme: currentTheme } = useTheme();

  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab && visibleTabs.indexOf(initialTab) >= 0) {
      return initialTab;
    }
    if (visibleTabs.length > 0) {
      return visibleTabs[0];
    }
    return 'person';
  });

  const [isMobile, setIsMobile] = useState(false);

  const tabsConfig = useMemo(() => {
    return [
      { id: 'person', label: 'Person', visible: visibleTabs.indexOf('person') >= 0 },
      { id: 'vehicle', label: 'Vehicle', visible: visibleTabs.indexOf('vehicle') >= 0 },
      { id: 'business', label: 'Business', visible: visibleTabs.indexOf('business') >= 0 },
      { id: 'client', label: 'Client', visible: visibleTabs.indexOf('client') >= 0 },
      { id: 'report', label: 'Report', visible: visibleTabs.indexOf('report') >= 0 },
      { id: 'callcard', label: 'Call Card', visible: visibleTabs.indexOf('callcard') >= 0 },
      { id: 'tourstop', label: 'Tour Stops', visible: visibleTabs.indexOf('tourstop') >= 0 },
    ];
  }, [visibleTabs]);

  const canSaveNpsdData = useMemo(() => {
    return currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE);
  }, []);

  const showNewButton = useMemo(() => {
    return searchMode === 'default' && canSaveNpsdData;
  }, [searchMode, canSaveNpsdData]);

  const officerLabel = useMemo(() => {
    return typeof getOfficerLabel === 'function' ? getOfficerLabel() : 'Officer';
  }, []);

  // Check mobile on mount and resize (debounced to avoid excessive re-renders)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 768), 150);
    };
    setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Set initial tab
  useEffect(() => {
    if (initialTab && visibleTabs.indexOf(initialTab) >= 0) {
      setActiveTab(initialTab);
    } else if (visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0]);
    }
  }, [initialTab, visibleTabs]);

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  }, [onTabChange]);

  // Navigate to entity detail page
  const navigateToEntity = useCallback((type: string, id: number) => {
    switch (type) {
      case 'person':
      case 'vehicle':
      case 'business':
      case 'client':
        navigateToRecord(type, id);
        return;
      case 'report':
        navigateToCaseReport(id, true, true);
        return;
      case 'callcard':
        navigateToCallCard(id);
        return;
      case 'tourstop':
        navigateToTourStopCompletion(id);
        return;
    }
  }, []);

  // Navigate to new entity creation
  const navigateToNewEntity = useCallback((type: string) => {
    switch (type) {
      case 'person':
        navigateToNewPerson();
        return;
      case 'vehicle':
        navigateToNewVehicle();
        return;
      case 'business':
        navigateToNewBusiness();
        return;
      case 'client':
        navigate({
          section: getModuleType().toUpperCase(),
          tab: 'Search',
          fn: 'Open_ClientGroup',
          multiModule: true,
        });
        return;
    }
  }, []);

  // Handle View action from search panels
  const handleView = useCallback((type: string, item: any) => {
    if (onView) {
      onView({ type, item });
    } else {
      navigateToEntity(type, item.id);
    }
  }, [onView, navigateToEntity]);

  // Handle Add action from search panels
  const handleAdd = useCallback((type: string, item: any) => {
    onAdd?.({ type, item });
  }, [onAdd]);

  // Handle Link action from search panels
  const handleLink = useCallback((type: string, item: any) => {
    onLink?.({ type, item });
  }, [onLink]);

  // Handle Open in New Tab action from search panels
  const handleOpenNewTab = useCallback((type: string, item: any) => {
    if (onOpenNewTab) {
      onOpenNewTab({ type, item });
    } else {
      // Default: open in new window (matches Vue Search.html behavior)
      const moduleType = getModuleType().toUpperCase();
      let url = '';
      if (type === 'report') {
        url = buildHashPath({
          section: moduleType,
          tab: 'CaseReports',
          fn: 'displayCaseReport',
          multiModule: true,
          params: String(item.id),
        });
      } else if (type === 'callcard') {
        // Use the same tab/fn pattern as navigateToCallCard
        if (moduleType === 'CAD') {
          url = buildHashPath({ section: 'CAD', tab: 'NewCallCard', fn: 'CAD_CallCard', multiModule: true, params: String(item.id) });
        } else {
          url = buildHashPath({ section: 'MDT', tab: 'SelfInitiate', fn: 'MDT_ShowCallCard', multiModule: true, params: String(item.id) });
        }
      }
      if (type === 'tourstop') {
        url = buildHashPath({ section: moduleType, tab: 'Search', fn: 'TourStopCompletion', multiModule: true, params: String(item.id) });
      }
      if (url) {
        openPageInNewWindow(url);
      }
    }
  }, [onOpenNewTab]);

  // Handle New button click
  const handleNew = useCallback((type: string) => {
    onNew?.(type);
    navigateToNewEntity(type);
  }, [onNew, navigateToNewEntity]);

  return (
    <div className={`npsd-search ${currentTheme}`}>
      {/* Tab Bar */}
      <NPSDTabBar
        tabs={tabsConfig}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Tab Panels */}
      <div className="npsd-search-panels">
        {/* Person Search */}
        <div className="npsd-search-panel" style={{ display: activeTab === 'person' ? undefined : 'none' }}>
          <NPSDSearchPerson
            searchMode={searchMode}
            isMobile={isMobile}
            theme={currentTheme}
            initialValues={initialValues}
            showNewButton={showNewButton}
            onView={(item) => handleView('person', item)}
            onAdd={(item) => handleAdd('person', item)}
            onLink={(item) => handleLink('person', item)}
            onNew={() => handleNew('person')}
          />
        </div>

        {/* Vehicle Search */}
        <div className="npsd-search-panel" style={{ display: activeTab === 'vehicle' ? undefined : 'none' }}>
          <NPSDSearchVehicle
            searchMode={searchMode}
            isMobile={isMobile}
            theme={currentTheme}
            initialValues={initialValues}
            showNewButton={showNewButton}
            onView={(item) => handleView('vehicle', item)}
            onAdd={(item) => handleAdd('vehicle', item)}
            onLink={(item) => handleLink('vehicle', item)}
            onNew={() => handleNew('vehicle')}
          />
        </div>

        {/* Business Search */}
        <div className="npsd-search-panel" style={{ display: activeTab === 'business' ? undefined : 'none' }}>
          <NPSDSearchBusiness
            searchMode={searchMode}
            isMobile={isMobile}
            theme={currentTheme}
            initialValues={initialValues}
            showNewButton={showNewButton}
            onView={(item) => handleView('business', item)}
            onAdd={(item) => handleAdd('business', item)}
            onNew={() => handleNew('business')}
          />
        </div>

        {/* Client Search */}
        <div className="npsd-search-panel" style={{ display: activeTab === 'client' ? undefined : 'none' }}>
          <NPSDSearchClient
            searchMode={searchMode}
            isMobile={isMobile}
            theme={currentTheme}
            initialValues={initialValues}
            showNewButton={showNewButton}
            onView={(item) => handleView('client', item)}
            onAdd={(item) => handleAdd('client', item)}
            onNew={() => handleNew('client')}
          />
        </div>

        {/* Report Search */}
        <div className="npsd-search-panel" style={{ display: activeTab === 'report' ? undefined : 'none' }}>
          <NPSDSearchReport
            searchMode={searchMode}
            isMobile={isMobile}
            theme={currentTheme}
            officerLabel={officerLabel}
            onView={(item) => handleView('report', item)}
            onAdd={(item) => handleAdd('report', item)}
            onOpenNewTab={(item) => handleOpenNewTab('report', item)}
          />
        </div>

        {/* Call Card Search */}
        <div className="npsd-search-panel" style={{ display: activeTab === 'callcard' ? undefined : 'none' }}>
          <NPSDSearchCallCard
            searchMode={searchMode}
            isMobile={isMobile}
            theme={currentTheme}
            officerLabel={officerLabel}
            onView={(item) => handleView('callcard', item)}
            onAdd={(item) => handleAdd('callcard', item)}
            onOpenNewTab={(item) => handleOpenNewTab('callcard', item)}
          />
        </div>

        {/* Tour Stop Completion Search */}
        <div className="npsd-search-panel" style={{ display: activeTab === 'tourstop' ? undefined : 'none' }}>
          <NPSDSearchTourStop
            searchMode={searchMode}
            isMobile={isMobile}
            theme={currentTheme}
            officerLabel={officerLabel}
            onView={(item) => handleView('tourstop', item)}
            onOpenNewTab={(item) => handleOpenNewTab('tourstop', item)}
          />
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDSearchReact = NPSDSearch;

// ── Screen registry ──────────────────────────────────────────────────────
